import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useSelector } from "react-redux";

import { toast } from "@performyard/py-components";

import type { EditableGoalFields, GoalSuggestion } from "../types";

import { useMutateCreateGoal } from "../../../api/hooks";

import { getGoalDefaultTransparency } from "../../../selectors/settings";

import { transformToGoalPayload } from "../utils/transformGoalSuggestion";
import { validateGoalFields } from "../utils/validateGoalFields";

import { useAskPerformYardStore } from "../AskPerformYardStore";

/**
 * Shared hook for goal creation logic.
 * Uses store-level `creatingSuggestionIds` so loading state is visible to all panels.
 */
export const useGoalCreation = () => {
	const goalSuggestionStates = useAskPerformYardStore((state) => state.goalSuggestionStates);
	const markGoalCreated = useAskPerformYardStore((state) => state.markGoalCreated);
	const markGoalCreating = useAskPerformYardStore((state) => state.markGoalCreating);
	const clearGoalCreating = useAskPerformYardStore((state) => state.clearGoalCreating);
	const setIsCreatingAll = useAskPerformYardStore((state) => state.setIsCreatingAll);

	const defaultTransparency = useSelector(getGoalDefaultTransparency);
	const queryClient = useQueryClient();
	const createGoalMutation = useMutateCreateGoal({});

	/** Convert EditableGoalFields to Partial<GoalSuggestion> for updating store display. */
	const toSuggestionEdits = (edits: Partial<EditableGoalFields>): Partial<GoalSuggestion> => {
		const result: Partial<GoalSuggestion> = {};
		if (edits.name !== undefined) result.name = edits.name;
		if (edits.target !== undefined) result.target = Number(edits.target);
		if (edits.units !== undefined) result.units = edits.units;
		if (edits.assignee !== undefined && edits.assignee) {
			result.assigneeId = edits.assignee.id;
			result.assigneeName = edits.assignee.name;
		}
		if (edits.startDate !== undefined) {
			result.startDate = edits.startDate ? edits.startDate.toISOString().split("T")[0] : "";
		}
		if (edits.endDate !== undefined) {
			result.endDate = edits.endDate ? edits.endDate.toISOString().split("T")[0] : "";
		}
		if (edits.categories !== undefined) {
			result.categories = edits.categories.map((c) => ({ id: c._id, name: c.name }));
		}
		return result;
	};

	/** Create a single goal from a suggestion (with optional edited values from the edit panel). */
	const handleCreateGoal = useCallback(
		async (suggestion: GoalSuggestion, edits?: Partial<EditableGoalFields>) => {
			markGoalCreating(suggestion.id);
			try {
				const payload = transformToGoalPayload(suggestion, edits, defaultTransparency);
				// GoalCreatePayload extends PostGoalData with internal API shape overrides
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const result = await createGoalMutation.mutateAsync(payload as any);
				const suggestionEdits = edits ? toSuggestionEdits(edits) : undefined;
				markGoalCreated(suggestion.id, result.goalID ?? result._id ?? result.id, suggestionEdits);
				queryClient.invalidateQueries({ queryKey: ["search-service", "goal"] });
			} catch {
				toast.error("Failed to create goal");
			} finally {
				clearGoalCreating(suggestion.id);
			}
		},
		[createGoalMutation, markGoalCreated, markGoalCreating, clearGoalCreating, queryClient, defaultTransparency]
	);

	/** Create a single goal by suggestion ID, using edited values from the store if available. */
	const handleCreateGoalById = useCallback(
		async (suggestionId: string) => {
			const gs = goalSuggestionStates.find((s) => s.suggestion.id === suggestionId);
			if (!gs || gs.status !== "pending") return;
			const edits = useAskPerformYardStore.getState().editedGoalFields[suggestionId];
			await handleCreateGoal(gs.suggestion, edits);
		},
		[goalSuggestionStates, handleCreateGoal]
	);

	/** Create all pending goals sequentially, using edited values from the store. */
	const handleCreateAllGoals = useCallback(async () => {
		const { editedGoalFields, triggerValidation } = useAskPerformYardStore.getState();
		const pending = goalSuggestionStates.filter((gs) => gs.status === "pending");

		// Validate all pending goals before creating any
		let hasErrors = false;
		for (const gs of pending) {
			const edits = editedGoalFields[gs.suggestion.id];
			const fieldsToValidate = edits ?? {
				name: gs.suggestion.name,
				target: String(gs.suggestion.target),
				units: gs.suggestion.units,
				startDate: gs.suggestion.startDate ? new Date(gs.suggestion.startDate + "T00:00:00") : null,
				endDate: gs.suggestion.endDate ? new Date(gs.suggestion.endDate + "T00:00:00") : null,
				categories: gs.suggestion.categories.map((c) => ({ _id: c.id, name: c.name })),
				assignee: { id: gs.suggestion.assigneeId, name: gs.suggestion.assigneeName },
			};
			const errors = validateGoalFields(fieldsToValidate);
			if (Object.keys(errors).length > 0) {
				hasErrors = true;
			}
		}

		if (hasErrors) {
			triggerValidation();
			return;
		}

		setIsCreatingAll(true);
		try {
			await Promise.allSettled(
				pending.map((gs) => {
					const edits = editedGoalFields[gs.suggestion.id];
					return handleCreateGoal(gs.suggestion, edits);
				})
			);
		} finally {
			setIsCreatingAll(false);
		}
	}, [goalSuggestionStates, handleCreateGoal, setIsCreatingAll]);

	return { handleCreateGoal, handleCreateGoalById, handleCreateAllGoals };
};
